/**
 * AnimatedNumber — a number that rolls to its new value (counts, EV %, odds).
 * Tabular digits so the width doesn't jitter. The first render shows the
 * final value (no count-up from 0 on page load unless `countUpOnMount`).
 *
 *   <AnimatedNumber value={271} style={s.count} />
 *   <AnimatedNumber value={ev} format={(v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}%`} />
 */
import React, { useEffect, useRef, useState } from 'react';
import { Text } from 'react-native';
import { DURATION, prefersReducedMotion } from '../../utils/motion';

const easeOut = (x) => 1 - Math.pow(1 - x, 3);
const defaultFormat = (v) => Math.round(v).toLocaleString('en-US');

export default function AnimatedNumber({ value, format = defaultFormat, duration = DURATION.slow + 120, countUpOnMount = false, style, ...rest }) {
    const target = Number.isFinite(Number(value)) ? Number(value) : 0;
    const [shown, setShown] = useState(countUpOnMount ? 0 : target);
    const fromRef = useRef(countUpOnMount ? 0 : target);
    const frameRef = useRef(null);

    useEffect(() => {
        const from = fromRef.current;
        if (from === target || prefersReducedMotion() || typeof requestAnimationFrame !== 'function') {
            fromRef.current = target;
            setShown(target);
            return undefined;
        }
        const start = Date.now();
        const tick = () => {
            const p = Math.min(1, (Date.now() - start) / duration);
            const v = from + (target - from) * easeOut(p);
            fromRef.current = v;
            setShown(v);
            if (p < 1) frameRef.current = requestAnimationFrame(tick);
        };
        frameRef.current = requestAnimationFrame(tick);
        return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
    }, [target, duration]);

    return (
        <Text style={[{ fontVariant: ['tabular-nums'] }, style]} {...rest}>
            {format(shown)}
        </Text>
    );
}
